import { createFileRoute, Outlet, useChildMatches } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import { listSessionsForCompany, assignSession, type AssessmentSessionDto } from '../../lib/assessmentSessions';
import { listAssessmentTemplates, type AssessmentTemplateDto } from '../../lib/assessmentTemplates';
import { listUsers, type UserDto } from '../../lib/users';
import { ClipboardCheck, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/kinerja/assessments')({
  component: AssessmentsPage,
});

function AssessmentsPage() {
  const childMatches = useChildMatches();
  if (childMatches.length > 0) {
    return (
      <ProtectedRoute minimumRole="employee">
        <Outlet />
      </ProtectedRoute>
    );
  }
  return (
    <ProtectedRoute minimumRole="hr">
      <AssessmentsContent />
    </ProtectedRoute>
  );
}

const STATUS_COLORS: Record<AssessmentSessionDto['status'], string> = {
  pending:     'secondary',
  in_progress: 'default',
  completed:   'success',
  expired:     'destructive',
};

const STATUS_LABELS: Record<AssessmentSessionDto['status'], string> = {
  pending:     'Pending',
  in_progress: 'In Progress',
  completed:   'Completed',
  expired:     'Expired',
};

function AssessmentsContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  const [sessions, setSessions] = useState<AssessmentSessionDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [templates, setTemplates] = useState<AssessmentTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<AssessmentSessionDto['status'] | 'all'>('all');

  // Assign dialog state
  const [showAssign, setShowAssign] = useState(false);
  const [assignTemplateId, setAssignTemplateId] = useState('');
  const [assignUserId, setAssignUserId] = useState('');
  const [assignDueDate, setAssignDueDate] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listSessionsForCompany(companyId, {}),
      listUsers(companyId, {}),
      listAssessmentTemplates(companyId, true),
    ]).then(([sessRes, usersRes, tplRes]) => {
      if (cancelled) return;
      setLoading(false);
      if (sessRes.success && sessRes.data) setSessions(sessRes.data.sessions);
      if (usersRes.success && usersRes.data) setUsers(usersRes.data.users);
      if (tplRes.success && tplRes.data) setTemplates(tplRes.data.templates);
    });
    return () => { cancelled = true; };
  }, [companyId]);

  const now = new Date();
  const isOverdue = (s: AssessmentSessionDto) =>
    s.status !== 'completed' && s.status !== 'expired' && s.dueDate && new Date(s.dueDate) < now;

  const getUserName = (id: string) => users.find((u) => u.id === id)?.name ?? id;

  const filtered = sessions.filter((s) => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (search) {
      const name = getUserName(s.assignedToUserId).toLowerCase();
      const tpl = (s.templateName ?? '').toLowerCase();
      if (!name.includes(search.toLowerCase()) && !tpl.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!assignTemplateId || !assignUserId) return;
    setAssigning(true);
    setAssignError('');
    setAssignSuccess('');
    const res = await assignSession(
      { companyId, templateId: assignTemplateId, assignedToUserId: assignUserId, assignedByUserId: user?.id, dueDate: assignDueDate || undefined },
    );
    setAssigning(false);
    if (res.success && res.data) {
      toast.success(`Assessment assigned to ${getUserName(assignUserId)}`);
      setAssignSuccess(`Assigned to ${getUserName(assignUserId)} successfully.`);
      setSessions((prev) => [res.data!.session, ...prev]);
      setAssignTemplateId('');
      setAssignUserId('');
      setAssignDueDate('');
    } else {
      toast.error(res.error ?? 'Failed to assign assessment');
      setAssignError(res.error ?? 'Failed to assign');
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">All Assessments</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Company-wide assessment sessions — {sessions.length} total
          </p>
        </div>
        <Button size="sm" onClick={() => { setShowAssign(true); setAssignSuccess(''); setAssignError(''); }}>
          <UserPlus className="w-4 h-4 mr-1.5" />
          Assign
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          className="w-full sm:w-60 border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Search employee or template…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as AssessmentSessionDto['status'] | 'all')}
        >
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-14 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-14 text-center">
            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center mb-3">
              <ClipboardCheck className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm font-medium text-foreground">No sessions found</p>
            <p className="text-xs text-muted-foreground mt-1">
              {search || statusFilter !== 'all' ? 'Try adjusting filters.' : 'Assign an assessment to get started.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Employee</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase hidden sm:table-cell">Template</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase hidden md:table-cell">Due Date</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase hidden md:table-cell">Assigned</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((s) => {
                  const overdue = isOverdue(s);
                  return (
                    <tr key={s.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground truncate">{getUserName(s.assignedToUserId)}</p>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell truncate max-w-[180px]">
                        {s.templateName ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Badge variant={STATUS_COLORS[s.status] as any}>
                            {STATUS_LABELS[s.status]}
                          </Badge>
                          {overdue && <Badge variant="destructive" className="text-[10px]">Overdue</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                        {s.dueDate ? new Date(s.dueDate).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                        {new Date(s.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Assign dialog */}
      <Dialog open={showAssign} onOpenChange={(open) => { if (!open) setShowAssign(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Assessment Session</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4 mt-1">
            <div className="space-y-1.5">
              <Label>Template</Label>
              <Select value={assignTemplateId} onValueChange={setAssignTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <Select value={assignUserId} onValueChange={setAssignUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee…" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} — {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-due">Due date (optional)</Label>
              <Input id="assign-due" type="date" value={assignDueDate} onChange={(e) => setAssignDueDate(e.target.value)} />
            </div>
            {assignError && <p className="text-xs text-destructive">{assignError}</p>}
            {assignSuccess && <p className="text-xs text-emerald-600">{assignSuccess}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssign(false)}>Close</Button>
              <Button type="submit" disabled={assigning || !assignTemplateId || !assignUserId}>
                {assigning ? 'Assigning…' : 'Assign Session'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
